/**
 * Badge stato mail AllService per singolo ordine.
 *
 * 3 stati visivi (Davide vuole "massimo controllo"):
 *  - 🟢 Confermata IMAP — scanner Sent ha trovato la mail in casella
 *  - 🟠 In attesa conferma — l'app ha inviato (SMTP) ma IMAP non ancora conferma
 *  - ⚪ Mai inviata — nessuna evidenza ne' app ne' IMAP
 *
 * Mostrato compatto in OrderCard, espanso in dettaglio.
 * Bottone "Verifica IMAP" triggera scan single-order on-demand.
 */

import { useState } from "react";
import { prettifyApiError } from "@/shared/api";
import {
  type AllServiceMailStatusDetail,
  type AllServiceMailStatusState,
  verifyAllServiceMailSent,
} from "../allserviceApi";

/** Tipo della funzione di verifica IMAP iniettabile.
 *  La firma è strutturalmente identica fra AllService e WebFurniture
 *  (entrambe restituiscono `{ status: AllServiceMailStatusDetail }`-compatibile);
 *  usare `unknown` sul resto dell'envelope evita vincoli sul tipo concreto
 *  del chiamante (order_id, reference, scan_metrics variano). */
type VerifyFn = (orderId: string) => Promise<{ status: AllServiceMailStatusDetail }>;

interface MailStatusBadgeProps {
  status: AllServiceMailStatusDetail;
  orderId: string;
  onUpdated?: (next: AllServiceMailStatusDetail) => void;
  variant?: "compact" | "expanded";
  /** Funzione di verifica IMAP da chiamare al click del bottone.
   *  Default: `verifyAllServiceMailSent` (retro-compat AS).
   *  Passa `verifyWebFurnitureMailSent` per i badge WF. */
  onVerify?: VerifyFn;
  /** Suffisso aggiunto a ogni `data-testid` emesso dal badge.
   *  Default `""` → i testid esistenti AS rimangono invariati.
   *  Usa `"-wf"` per i badge WebFurniture così da evitare collisioni
   *  quando AS e WF sono entrambi presenti sullo stesso ordine. */
  testIdSuffix?: string;
}

const STATE_PRESETS: Record<
  AllServiceMailStatusState,
  { label: string; dotClass: string; pillClass: string }
> = {
  confirmed_imap: {
    label: "Confermata IMAP",
    dotClass: "bg-green",
    pillClass: "bg-green-soft text-ink border border-green/30",
  },
  pending_imap: {
    label: "In attesa conferma",
    dotClass: "bg-amber",
    pillClass: "bg-amber-soft text-ink border border-amber/40",
  },
  never_sent: {
    label: "Mail mai inviata",
    dotClass: "bg-ink-3",
    pillClass: "bg-paper-2 text-ink-3 border border-line-2",
  },
};

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function MailStatusBadge({
  status,
  orderId,
  onUpdated,
  variant = "compact",
  onVerify,
  testIdSuffix = "",
}: MailStatusBadgeProps) {
  const [verifying, setVerifying] = useState(false);
  // Esito dell'ultimo scan (riuscito o fallito). Senza, il tasto non dava alcun
  // segnale di successo: i chiamanti reali (ActivitySection) non passano
  // `onUpdated` e lo stato a schermo è derivato da `order.raw`, quindi dopo il
  // click non cambiava nulla — né quando la mail veniva trovata né, tanto meno,
  // nel caso più frequente in cui non c'è. L'operatore ripremeva convinto che
  // il tasto fosse rotto.
  const [esito, setEsito] = useState<{ ok: boolean; testo: string } | null>(null);
  const preset = STATE_PRESETS[status.state];

  async function handleVerify() {
    setVerifying(true);
    setEsito(null);
    try {
      const verifyFn = onVerify ?? verifyAllServiceMailSent;
      const result = await verifyFn(orderId);
      onUpdated?.(result.status);
      setEsito({
        ok: true,
        testo:
          result.status.state === "confirmed_imap"
            ? `Mail trovata in Sent (${formatDateTime(result.status.imap_confirmed_at)}).`
            : "Scan completato: nessuna mail per questo ordine nella cartella Sent.",
      });
    } catch (err) {
      setEsito({ ok: false, testo: prettifyApiError(err) });
    } finally {
      setVerifying(false);
    }
  }

  if (variant === "compact") {
    const tooltip = [
      `Stato: ${preset.label}`,
      status.app_sent_at ? `App inviata: ${formatDateTime(status.app_sent_at)}` : "",
      status.imap_confirmed_at ? `IMAP conferma: ${formatDateTime(status.imap_confirmed_at)}` : "",
      status.app_attempts > 1 ? `Tentativi app: ${status.app_attempts}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <span
        data-testid={`mail-status-badge-${orderId}${testIdSuffix}`}
        title={tooltip}
        className={`inline-flex items-center gap-1.5 px-2 py-px rounded-full text-[10.5px] font-medium ${preset.pillClass}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${preset.dotClass}`} />
        {preset.label}
      </span>
    );
  }

  return (
    <div
      data-testid={`mail-status-detail-${orderId}${testIdSuffix}`}
      className="rounded-md border border-line bg-paper px-3 py-2.5 text-[12px]"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-px rounded-full text-[11px] font-medium ${preset.pillClass}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${preset.dotClass}`} />
          {preset.label}
        </span>
        <button
          type="button"
          onClick={() => void handleVerify()}
          disabled={verifying}
          data-testid={`mail-status-verify-${orderId}${testIdSuffix}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-medium bg-card text-ink-2 border border-line hover:border-ink-4 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          title="Forza scan IMAP cartella Sent per questo ordine"
        >
          {verifying ? "Verifico IMAP..." : "Verifica IMAP"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-ink-2 font-mono text-[11px] tabular-nums">
        <div>
          <span className="text-ink-3">App inviata:</span>{" "}
          {status.app_sent_at ? formatDateTime(status.app_sent_at) : "—"}
        </div>
        <div>
          <span className="text-ink-3">IMAP conferma:</span>{" "}
          {status.imap_confirmed_at ? formatDateTime(status.imap_confirmed_at) : "—"}
        </div>
        <div>
          <span className="text-ink-3">Tentativi app:</span> {status.app_attempts}
        </div>
        <div>
          <span className="text-ink-3">Destinatario:</span>{" "}
          <span className="text-[10.5px]">{status.recipient}</span>
        </div>
        {(status.imap_attachment || status.app_attachment) && (
          <div className="col-span-2">
            <span className="text-ink-3">Allegato:</span>{" "}
            {status.imap_attachment || status.app_attachment}
          </div>
        )}
        {status.imap_message_id && (
          <div className="col-span-2 truncate" title={status.imap_message_id}>
            <span className="text-ink-3">Message-ID:</span> {status.imap_message_id}
          </div>
        )}
      </div>
      {esito && (
        <div
          role="status"
          data-testid={`mail-status-esito-${orderId}${testIdSuffix}`}
          className={`mt-2 text-[11px] ${esito.ok ? "text-ink-3" : "text-red"}`}
        >
          {esito.testo}
        </div>
      )}
    </div>
  );
}
